import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { helpdeskApi, type HelpdeskTicketDto, type HelpdeskAssignees } from '../api/helpdesk';
import { listSpaces, type SpaceListItem } from '../api/auth';
import { useAuth } from '../auth/AuthContext';
import { HelpdeskDetail } from './HelpdeskDetail';
import { HelpdeskSettings } from './settings/HelpdeskSettings';
import { HelpdeskStats } from './stats/HelpdeskStats';
import { useFeather } from '../shell/useFeather';
import { ticketCodeLabel } from './ticket-code';
import { BarChart2, Settings } from 'lucide-react';
import { Button } from '../components/arc/button/button';
import { ArcPage, ArcPageHeader, ArcPanel, ArcSummaryGrid, ArcSummaryMetric, ArcToolbar } from '../components/arc/primitives/arc-primitives';
import './helpdesk.css';
import './helpdesk-arc.css';

export { HD_STATUS } from './status';
import { HD_STATUS } from './status';
import { confirmDialog, noticeDialog } from '../shell/dialogs';
import { CompactSelect } from '../components/arc/compact-select/compact-select';

const COUNTRY: Record<string, string> = { cs: 'CZ', sk: 'SK', ro: 'RO', en: 'EN' };

const LIST_REFRESH_MS = 20000;

const STATUS_FILTER: { value: string; label: string }[] = [
  { value: 'aktivni', label: 'Aktivní' },
  { value: 'novy', label: 'Nové' },
  { value: 'otevreny', label: 'Otevřené' },
  { value: 'ceka_na_uziv', label: 'Čeká na uživatele' },
  { value: 'vyreseno', label: 'Vyřešené' },
  { value: 'zavreno', label: 'Zavřené' },
  { value: 'spam', label: 'Spam' },
  { value: '', label: 'Vše' },
];


function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'právě teď';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} d`;
  return new Date(iso).toLocaleDateString('cs-CZ');
}

function pluralTickets(n: number): string {
  if (n === 1) return '1 ticket';
  if (n >= 2 && n <= 4) return `${n} tickety`;
  return `${n} ticketů`;
}

export function HelpdeskPage({ lockedSpaceId }: { lockedSpaceId?: string | number } = {}) {
  const { me } = useAuth();
  const canManageHelpdesk = !!me?.canAccessAllSpacesInTenant || !!me?.permissionKeys?.includes('helpdesk:admin');
  const [tickets, setTickets] = useState<HelpdeskTicketDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'settings' | 'stats'>('list');
  const [assignees, setAssignees] = useState<HelpdeskAssignees>({ users: [], aiAgents: [] });
  const [openKebab, setOpenKebab] = useState<string | null>(null);

  useEffect(() => {
    helpdeskApi.assignees().then(setAssignees).catch(() => {});
  }, []);

  const [spaceFilter, setSpaceFilter] = useState(lockedSpaceId != null ? String(lockedSpaceId) : '');
  const [statusFilter, setStatusFilter] = useState('aktivni');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [aiOnly, setAiOnly] = useState(false);
  const [copilot, setCopilot] = useState(() => localStorage.getItem('hd-copilot') === '1');
  const [search, setSearch] = useState('');
  const [spaceLabels, setSpaceLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    listSpaces()
      .then((list: SpaceListItem[]) => {
        if (!active) return;
        const map: Record<string, string> = {};
        for (const s of list) map[String(s.id)] = s.name;
        setSpaceLabels(map);
      })
      .catch(() => { /* silently ignore — fallback to "Space {id}" label */ });
    return () => { active = false; };
  }, []);

  useEffect(() => { localStorage.setItem('hd-copilot', copilot ? '1' : '0'); }, [copilot]);

  // Deep-link z MM notifikace (/helpdesk?ticketId=…): otevři rovnou detail a
  // uklid parametr z adresy, ať se ticket po zavření znovu neotevírá.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('ticketId');
    if (!raw || !/^[1-9]\d*$/.test(raw)) return;
    setActiveId(raw);
    params.delete('ticketId');
    const clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
    window.history.replaceState({}, '', clean);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauth = params.get('oauth');
    if (!oauth) return;
    const email = params.get('email');
    const message = params.get('message');
    const accountIdRaw = params.get('accountId');
    if (oauth === 'success' && email) {
      void noticeDialog({ title: 'Gmail připojen', message: `Účet ${email} je připojen. Spouštím sledování inboxu…` });
      const accountId = accountIdRaw ? Number(accountIdRaw) : null;
      if (accountId && Number.isFinite(accountId)) {
        (async () => {
          try {
            const { helpdeskAdminApi } = await import('../api/helpdesk-admin');
            await helpdeskAdminApi.startGmailWatch(accountId);
            await helpdeskAdminApi.initialGmailImport(accountId, 20);
          } catch { /* silent - user can retry from settings */ }
        })();
      }
    } else if (oauth === 'error') void noticeDialog({ title: 'Připojení Gmailu se nezdařilo', message: message ?? 'Neznámá chyba.' });
    params.delete('oauth'); params.delete('email'); params.delete('message'); params.delete('accountId');
    const clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
    window.history.replaceState({}, '', clean);
  }, []);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceFilter, statusFilter, assigneeFilter, urgentOnly, aiOnly]);

  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Live refresh of the queue: a silent reload on an interval plus one on tab
  // focus, so a newly ingested ticket appears without the operator acting.
  // Paused while a detail or a row menu is open so the list never shifts under
  // an interaction, and while the tab is hidden.
  useEffect(() => {
    if (view !== 'list') return;
    const paused = () => document.hidden || activeId !== null || openKebab !== null;
    const tick = () => { if (!paused()) void load({ silent: true }); };
    const id = window.setInterval(tick, LIST_REFRESH_MS);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, activeId, openKebab, spaceFilter, statusFilter, assigneeFilter, urgentOnly, aiOnly, search]);

  async function load({ silent = false }: { silent?: boolean } = {}) {
    if (!silent) setLoading(true);
    try {
      const result = await helpdeskApi.list({
        spaceId: (lockedSpaceId != null ? String(lockedSpaceId) : spaceFilter) || undefined,
        status: statusFilter || undefined,
        assignee: assigneeFilter && assigneeFilter !== 'ai' ? assigneeFilter : undefined,
        urgent: urgentOnly || undefined,
        aiOnly: aiOnly || assigneeFilter === 'ai' || undefined,
        search: search.trim() || undefined,
      });
      setTickets(result.tickets);
      setError(null);
    } catch (err) {
      if (!silent) setError(err instanceof Error ? err.message : 'Chyba načítání');
    } finally {
      if (!silent) setLoading(false);
    }
  }

  const spaceOptions = useMemo(() => {
    const ids = new Set(tickets.map((t) => t.primarySpaceId));
    return [...ids].sort();
  }, [tickets]);

  const queueSummary = useMemo(() => ({
    newCount: tickets.filter((ticket) => ticket.status === 'novy').length,
    waitingCount: tickets.filter((ticket) => ticket.status === 'ceka_na_uziv').length,
    urgentCount: tickets.filter((ticket) => ticket.urgent).length,
  }), [tickets]);

  useFeather([view, tickets.length, loading]);

  if (view === 'settings') {
    return <HelpdeskSettings onClose={() => setView('list')} />;
  }
  if (view === 'stats') {
    return <HelpdeskStats onClose={() => setView('list')} />;
  }

  return (
    <ArcPage className="hd-page hd-arc-page">
      <ArcPageHeader
        eyebrow="PODPORA"
        title="Helpdesk"
        description="Jedno místo pro požadavky zákazníků a práci týmu."
        meta={<div className="hd-header-actions">
          <label className={`hd-copilot-toggle${copilot ? ' on' : ''}`} title="Copilot - AI navrhuje odpovědi (zapne se se zapojením AI)">
            <input type="checkbox" className="hd-copilot-input" checked={copilot} onChange={(e) => setCopilot(e.target.checked)} />
            <span className="hd-copilot-slider" />
            <span className="hd-copilot-label">Copilot</span>
          </label>
          <Button type="button" variant="secondary" size="sm" onClick={() => setView('stats')}><BarChart2 size={16} /> Statistiky</Button>
          {canManageHelpdesk && (
            <Button type="button" variant="ghost" size="sm" className="hd-arc-settings" onClick={() => setView('settings')} title="Nastavení podpory" aria-label="Nastavení"><Settings size={17} /></Button>
          )}
        </div>}
      />

      <div className="hd-vocative-banner" role="status">
        <span className="hd-vocative-banner-icon" aria-hidden="true">🔬</span>
        <span>
          <strong>Testovací režim párování kontaktů.</strong>{' '}
          Odchozí e-maily jdou zatím bez oslovení. Sledujte dlaždici „Kontakt&#8221; na kartě ticketu — až bude párování ověřené, oslovení do mailů zase pustíme.
        </span>
      </div>

      <ArcSummaryGrid className="hd-arc-overview">
        <ArcSummaryMetric label="Aktivní fronta" value={tickets.length} context="" />
        <ArcSummaryMetric label="Nové" value={queueSummary.newCount} context="" />
        <ArcSummaryMetric label="Čeká na zákazníka" value={queueSummary.waitingCount} context="" />
        <ArcSummaryMetric label="Urgentní" value={queueSummary.urgentCount} context="" />
      </ArcSummaryGrid>

      <ArcToolbar className="hd-arc-toolbar" label="Filtry Helpdesku" end={<div className="hd-arc-toolbar-end">
        <input type="search" className="hd-search" aria-label="Hledat tickety" placeholder="Hledat…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <span className="hd-list-summary hd-toolbar-count">{pluralTickets(tickets.length)}</span>
      </div>}>
        <div className="hd-arc-filter-fields">
          {lockedSpaceId == null && (
            <CompactSelect className="hd-toolbar-select" value={spaceFilter} onChange={setSpaceFilter} ariaLabel="Space" options={[{ value: '', label: 'Vše napříč Spaces' }, ...spaceOptions.map((id) => ({ value: String(id), label: spaceLabels[id] ?? `Space ${id}` }))]} />
          )}
          <CompactSelect className="hd-toolbar-select" value={statusFilter} onChange={setStatusFilter} ariaLabel="Stav ticketu" options={STATUS_FILTER} />
          <CompactSelect className="hd-toolbar-select" value={assigneeFilter} onChange={setAssigneeFilter} ariaLabel="Přiřazení" options={[{ value: '', label: 'Všichni' }, { value: 'me', label: 'Jen moje' }, { value: 'unassigned', label: 'Nepřiřazené' }, { value: 'ai', label: 'AI asistent' }]} />
          <label className="hd-toggle">
            <input type="checkbox" checked={urgentOnly} onChange={(e) => setUrgentOnly(e.target.checked)} />
            Urgentní
          </label>
          <label className="hd-toggle">
            <input type="checkbox" checked={aiOnly} onChange={(e) => setAiOnly(e.target.checked)} />
            Jen AI
          </label>
        </div>
      </ArcToolbar>

      {loading && <p className="hd-empty">Načítám tickety…</p>}
      {error && <p className="hd-error">{error}</p>}
      {!loading && !error && tickets.length === 0 && <p className="hd-empty">Žádné tickety.</p>}

      {!loading && !error && tickets.length > 0 && (
        <ArcPanel as="div" className="hd-list hd-arc-list">
          <div className="hd-list-head" aria-hidden="true">
            <span>Požadavek</span><span>Řešitel</span><span>Stav</span><span>Aktualizace</span><span />
          </div>
          {tickets.map((t) => {
            const st = HD_STATUS[t.status] ?? { label: t.status, color: '#6c6c70' };
            return (
              <div key={t.id} className="hd-ticket-row" role="button" tabIndex={0} onClick={() => setActiveId(t.id)} onKeyDown={(e) => { if (e.key === 'Enter') setActiveId(t.id); }}>
                <span className="hd-urgent-dot" style={{ visibility: t.urgent ? 'visible' : 'hidden' }} />
                <span className="hd-tr-main">
                  <span className="hd-tr-line">
                    <span className="hd-tr-code">{ticketCodeLabel(t)}</span>
                    <span className="hd-tr-from">{t.fromName ?? t.fromEmail}</span>
                    {COUNTRY[t.accountLanguage] && <span className="hd-country-chip">{COUNTRY[t.accountLanguage]}</span>}
                    <span className="hd-tr-subject">- {t.subject}</span>
                    {t.aiHandled && <span className="hd-ai-badge">✓ AI</span>}
                    {t.tags.map((tag) => (
                      <span key={tag.id} className="hd-tr-tag" style={{ background: `${tag.color}20`, color: tag.color }}>{tag.name}</span>
                    ))}
                  </span>
                  <span className="hd-tr-meta">{t.fromEmail}</span>
                </span>
                <span className="hd-tr-assignee">
                  {t.assigneeType === 'ai'
                    ? <span className="hd-assignee-ai">AI · {t.assigneeUserName ?? 'AI asistent'}</span>
                    : t.assigneeUserName ?? '-'}
                </span>
                <span
                  className="hd-status-badge"
                  style={{ color: st.color, background: `${st.color}1f` }}
                >
                  {st.label}
                </span>
                <span className="hd-tr-time">{relativeTime(t.lastMessageAt ?? t.createdAt)}</span>
                <TicketKebab
                  ticket={t}
                  assignees={assignees}
                  open={openKebab === t.id}
                  onToggle={() => setOpenKebab((cur) => (cur === t.id ? null : t.id))}
                  onDone={() => { setOpenKebab(null); void load(); }}
                />
              </div>
            );
          })}
        </ArcPanel>
      )}

      {activeId && (
        <HelpdeskDetail ticketId={activeId} onClose={() => setActiveId(null)} onChanged={() => void load()} />
      )}
    </ArcPage>
  );
}

const KEBAB_STATUSES = ['novy', 'otevreny', 'ceka_na_uziv', 'vyreseno', 'zavreno', 'spam'];

function TicketKebab({
  ticket,
  assignees,
  open,
  onToggle,
  onDone,
}: {
  ticket: HelpdeskTicketDto;
  assignees: HelpdeskAssignees;
  open: boolean;
  onToggle: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const place = () => {
      const btn = btnRef.current?.getBoundingClientRect();
      if (!btn) return;
      const menuH = menuRef.current?.offsetHeight ?? 0;
      const below = btn.bottom + 2;
      const top = below + menuH > window.innerHeight - 8 && btn.top - menuH - 2 > 8 ? btn.top - menuH - 2 : below;
      setPos({ top: Math.max(8, Math.min(top, window.innerHeight - menuH - 8)), right: Math.max(8, window.innerWidth - btn.right) });
    };
    place();
    const close = (e: Event) => {
      if (e.target instanceof Node && menuRef.current?.contains(e.target)) return;
      onToggle();
    };
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const run = (e: React.MouseEvent, fn: () => Promise<unknown>) => {
    e.stopPropagation();
    setBusy(true);
    fn()
      .then(onDone)
      .catch((err) => void noticeDialog({ title: 'Akci se nepodařilo provést', message: err instanceof Error ? err.message : 'Neznámá chyba.' }))
      .finally(() => setBusy(false));
  };
  const assigneeVal = ticket.assigneeType === 'ai' ? `ai:${ticket.assigneeAiAgentId}` : ticket.assigneeUserId ? `user:${ticket.assigneeUserId}` : '';
  return (
    <span className="hd-row-actions" onClick={(e) => e.stopPropagation()}>
      <button ref={btnRef} type="button" className="hd-kebab-btn" aria-label="Akce" onClick={(e) => { e.stopPropagation(); onToggle(); }}>⋮</button>
      {open && (
        <div
          ref={menuRef}
          className="hd-kebab-menu"
          style={pos ? { position: 'fixed', top: pos.top, right: pos.right } : { position: 'fixed', visibility: 'hidden' }}
        >
          <div className="hd-kebab-head">Stav</div>
          {KEBAB_STATUSES.map((s) => (
            <button key={s} type="button" className="hd-kebab-item" disabled={busy} onClick={(e) => run(e, () => helpdeskApi.setStatus(ticket.id, s))}>
              <span className="hd-kebab-check">{ticket.status === s ? '✓' : ''}</span>{HD_STATUS[s]?.label ?? s}
            </button>
          ))}
          <div className="hd-kebab-sep" />
          <button type="button" className="hd-kebab-item" disabled={busy} onClick={(e) => run(e, () => helpdeskApi.setUrgent(ticket.id, !ticket.urgent))}>
            <span className="hd-kebab-check">{ticket.urgent ? '✓' : ''}</span>{ticket.urgent ? 'Zrušit urgentní' : 'Označit urgentní'}
          </button>
          <div className="hd-kebab-sep" />
          <div className="hd-kebab-head">Přiřadit</div>
          <button type="button" className="hd-kebab-item" disabled={busy} onClick={(e) => run(e, () => helpdeskApi.setAssignee(ticket.id, null))}>
            <span className="hd-kebab-check">{!assigneeVal ? '✓' : ''}</span>Nepřiřazeno
          </button>
          {assignees.users.map((u) => (
            <button key={`u${u.id}`} type="button" className="hd-kebab-item" disabled={busy} onClick={(e) => run(e, () => helpdeskApi.setAssignee(ticket.id, { type: 'user', userId: u.id }))}>
              <span className="hd-kebab-check">{assigneeVal === `user:${u.id}` ? '✓' : ''}</span>{u.name}
            </button>
          ))}
          {assignees.aiAgents.map((a) => (
            <button key={`a${a.id}`} type="button" className="hd-kebab-item" disabled={busy} onClick={(e) => run(e, () => helpdeskApi.setAssignee(ticket.id, { type: 'ai', aiAgentId: a.id }))}>
              <span className="hd-kebab-check">{assigneeVal === `ai:${a.id}` ? '✓' : ''}</span>{a.name}
            </button>
          ))}
          <div className="hd-kebab-sep" />
          <button type="button" className="hd-kebab-item hd-kebab-danger" disabled={busy} onClick={(e) => { e.stopPropagation(); void confirmDialog({ title: 'Archivovat ticket?', confirmLabel: 'Archivovat' }).then((ok) => { if (ok) run(e, () => helpdeskApi.archive(ticket.id)); }); }}>
            Smazat ticket
          </button>
        </div>
      )}
    </span>
  );
}
